<template>
  <div class="wcd-appointment-finder" :class="{ 'light-theme': !isDarkMode }">
    <div class="wcd-finder-scroll-area pa-4">
      <!-- Title Row -->
      <div v-if="!hideTitle" class="wcd-finder-title-row mb-3 d-flex align-center">
        <v-icon size="18" color="primary" class="mr-2">mdi-calendar-search</v-icon>
        <span class="text-subtitle-2 font-weight-bold" :class="isDarkMode ? 'white--text' : 'grey--text text--darken-4'"
          >Termin-Finder Dashboard</span
        >
      </div>

      <!-- Render Finder Control Panel -->
      <div class="wcd-finder-controls-box mb-4">
        <!-- Filter Mitarbeiter & Datum Row -->
        <div class="d-flex gap-2 mb-3" style="gap: 8px">
          <div class="flex-grow-1">
            <div class="wcd-time-label">Mitarbeiter</div>
            <v-select
              v-model="finderColId"
              :items="[{ id: 'all', name: 'Alle' }, ...employees.filter((e) => e.id !== 'ma_info' && e.id !== 'no_show')]"
              item-text="name"
              item-value="id"
              dense
              solo
              flat
              :dark="isDarkMode"
              hide-details
              :background-color="isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'"
              class="wcd-time-input"
              style="font-size: 0.8rem"></v-select>
          </div>

          <div style="width: 150px; flex-shrink: 0">
            <div class="wcd-time-label">Startdatum</div>
            <v-menu v-model="finderDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="wcd-date-display"
                  v-bind="attrs"
                  v-on="on"
                  :style="{
                    height: '36px',
                    background: isDarkMode ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.04)',
                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.08)',
                    color: isDarkMode ? 'white' : '#1e1e1e',
                  }">
                  <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                  <span style="font-size: 0.75rem">{{ formatDateFriendly(finderStartDate) }}</span>
                </div>
              </template>
              <v-date-picker v-model="finderStartDate" no-title @input="finderDateMenu = false" locale="de" :dark="isDarkMode"></v-date-picker>
            </v-menu>
          </div>
        </div>

        <!-- Tageszeit Filter Chips -->
        <div class="mb-3">
          <div class="wcd-time-label">Tageszeit</div>
          <div class="wcd-time-chips">
            <div
              v-for="opt in timeOfDayOptions"
              :key="opt.value"
              class="wcd-time-chip"
              :class="{ active: filterTimeOfDay === opt.value }"
              @click="filterTimeOfDay = opt.value">
              <v-icon x-small class="mr-1" color="inherit">{{ opt.icon }}</v-icon>
              <span>{{ opt.label }}</span>
            </div>
          </div>
        </div>

        <!-- Weekdays Filter selector & Ignore Tasks Row -->
        <div class="d-flex align-end justify-space-between mb-1" style="gap: 12px">
          <div class="flex-grow-1">
            <div class="wcd-time-label">Wochentage</div>
            <div class="wcd-weekday-selector">
              <div
                v-for="day in weekdayOptions"
                :key="day.value"
                class="wcd-weekday-chip"
                :class="{ active: filterWeekdays.includes(day.value) }"
                @click="toggleWeekday(day.value)">
                {{ day.label }}
              </div>
            </div>
          </div>

          <div style="min-width: 90px; flex-shrink: 0">
            <div class="wcd-time-label">Aufgaben</div>
            <div
              class="wcd-pill-checkbox"
              :class="{ active: finderIgnoreTasks }"
              @click="finderIgnoreTasks = !finderIgnoreTasks"
              style="height: 34px">
              <v-icon x-small class="mr-1">{{ finderIgnoreTasks ? "mdi-eye-off" : "mdi-eye-outline" }}</v-icon>
              <span style="font-size: 0.75rem">Ignorieren</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Date warning indicator if different date is selected -->
      <div v-if="date && date !== finderStartDate" class="wcd-date-notice mb-3">
        <v-icon x-small color="primary" class="mr-1">mdi-information-outline</v-icon>
        <span
          >Termin wird am <strong>{{ formatDateFriendly(date) }}</strong> erstellt/verschoben.</span
        >
      </div>

      <!-- Results container -->
      <div class="wcd-finder-results-box flex-grow-1">
        <!-- Loading Indicator -->
        <div v-if="loadingSlots" class="wcd-loader-container">
          <v-progress-circular indeterminate color="primary" size="28" width="2"></v-progress-circular>
          <div class="mt-2 text-caption text--secondary">Suche freie Zeiten...</div>
        </div>

        <!-- Empty State -->
        <div v-else-if="suggestedSlots.length === 0" class="wcd-empty-container">
          <v-icon size="32" :color="isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'">mdi-calendar-question</v-icon>
          <div class="mt-2 text-caption text--secondary text-center px-4">Keine freien Zeiten mit diesen Filtern gefunden.</div>
        </div>

        <!-- Grouped Slots (Timeline & Suggestions) -->
        <div v-else class="wcd-slots-list">
          <div v-for="group in slotsGroupedByDay" :key="group.date" class="wcd-day-group pa-2">
            <div class="wcd-day-header d-flex justify-space-between align-center mb-2">
              <span class="font-weight-bold" :class="isDarkMode ? 'white--text' : 'grey--text text--darken-3'" style="font-size: 0.75rem">{{
                group.dayName
              }}</span>
              <span class="wcd-day-date grey--text" style="font-size: 0.7rem">{{ formatDateFriendly(group.date) }}</span>
            </div>

            <!-- Visual Timelines for the Day -->
            <div class="wcd-timelines-container mb-3">
              <div v-for="timeline in group.timelines" :key="timeline.id" class="wcd-timeline-row">
                <div class="wcd-timeline-emp-info">
                  <v-avatar size="24" class="mr-2">
                    <img :src="getEmployeePictureUrl(timeline.picture)" alt="avatar" />
                  </v-avatar>
                  <span class="wcd-timeline-emp-name">{{ timeline.name }}</span>
                </div>

                <!-- Visual segment bar -->
                <div class="wcd-timeline-bar-wrapper">
                  <!-- Hour indicators (Ruler lines) -->
                  <div class="wcd-timeline-ruler">
                    <div class="wcd-ruler-tick" style="left: 0%"><span>8h</span></div>
                    <div class="wcd-ruler-tick" style="left: 25%"><span>11h</span></div>
                    <div class="wcd-ruler-tick" style="left: 50%"><span>14h</span></div>
                    <div class="wcd-ruler-tick" style="left: 75%"><span>17h</span></div>
                    <div class="wcd-ruler-tick" style="left: 100%"><span>20h</span></div>
                  </div>

                  <div class="wcd-timeline-bar" style="position: relative">
                    <!-- Grid lines overlay -->
                    <div class="wcd-timeline-grid-lines">
                      <div class="wcd-grid-line" style="left: 25%"></div>
                      <div class="wcd-grid-line" style="left: 50%"></div>
                      <div class="wcd-grid-line" style="left: 75%"></div>
                    </div>
                    <template v-for="(seg, idx) in timeline.segments">
                      <v-tooltip bottom :key="idx" :disabled="isTouchDevice">
                        <template v-slot:activator="{ on, attrs }">
                          <div
                            v-bind="attrs"
                            v-on="on"
                            :class="['wcd-segment', 'segment-' + seg.type]"
                            :style="{ width: seg.widthPct + '%' }"
                            @click="seg.type === 'free' || seg.type === 'task' ? selectFreeSegment(group.date, timeline, seg) : null">
                            <!-- Hover visual highlight -->
                            <div class="wcd-segment-highlight" v-if="seg.type === 'free' || seg.type === 'task'"></div>
                          </div>
                        </template>
                        <span>{{ seg.label }}: {{ seg.timeStr }}</span>
                      </v-tooltip>
                    </template>

                    <!-- Active Selection Overlay -->
                    <div
                      v-if="activeSegment && activeSegment.colId === timeline.id && activeSegment.date === group.date"
                      class="wcd-active-selection-overlay"
                      :style="activeSelectionStyle"
                      @mousedown.stop="initiateDrag($event, group.date, timeline)"
                      @touchstart.stop="initiateDrag($event, group.date, timeline)">
                      <div class="wcd-drag-handle-left"></div>
                      <div class="wcd-drag-handle-right"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Active Segment Shortcuts (Sticky at Bottom) -->
    <v-expand-transition>
      <div v-if="activeSegment" class="wcd-active-segment-box pa-4">
        <!-- Close button -->
        <div class="d-flex justify-space-between align-start mb-2" style="position: absolute; right: 12px; top: 12px; z-index: 15">
          <v-btn icon x-small @click="activeSegment = null" :style="{ color: isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.5)' }">
            <v-icon x-small>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Desktop Layout (3 Columns) -->
        <div v-if="!$vuetify.breakpoint.smAndDown" class="d-flex flex-wrap" style="gap: 24px">
          <!-- Column 1: Trainer Profile -->
          <div class="flex-grow-1" style="min-width: 200px; max-width: 260px">
            <div class="d-flex align-center mb-2" style="gap: 12px">
              <v-avatar size="44" class="elevation-3" style="border: 2px solid var(--v-primary-base)">
                <img :src="getEmployeePictureUrl(activeSegment.colPicture)" alt="avatar" />
              </v-avatar>
              <div>
                <div
                  class="font-weight-black text-shadow"
                  :class="isDarkMode ? 'white--text' : 'grey--text text--darken-4'"
                  style="font-size: 0.95rem; line-height: 1.2">
                  {{ activeSegment.colName }}
                </div>
                <div class="text-caption grey--text font-weight-medium mt-1">
                  {{ formatDateFriendly(activeSegment.date) }}
                </div>
              </div>
            </div>
            <div class="wcd-pill-range mt-2">
              <v-icon x-small color="primary" class="mr-1">mdi-clock-outline</v-icon>
              <span>Frei: {{ minutesToTimeStr(activeSegment.startMin) }} - {{ minutesToTimeStr(activeSegment.endMin) }}</span>
            </div>
          </div>

          <!-- Column 2: Startzeit Grid -->
          <div class="flex-grow-1" style="min-width: 280px; max-width: 420px">
            <div class="wcd-shortcuts-title mb-2 d-flex align-center" style="gap: 4px">
              <v-icon x-small color="primary">mdi-gesture-tap</v-icon>
              <span>Startzeit wählen:</span>
            </div>
            <div class="wcd-shortcuts-grid">
              <div
                v-for="slot in activeSegmentSlots"
                :key="slot.time"
                class="wcd-shortcut-btn d-flex align-center"
                :class="{ active: start === slot.time, 'optimal-connect': slot.isOptimal }"
                @click="selectShortcutTime(slot.time)">
                <span>{{ slot.time }}</span>
                <v-icon v-if="slot.isOptimal" x-small color="success" class="ml-1" style="font-size: 11px">mdi-check-decagram</v-icon>
              </div>
              <div v-if="activeSegmentSlots.length === 0" class="grey--text text-caption py-1">
                Keine passende Startzeit für die Dauer von {{ duration }} Min vorhanden.
              </div>
            </div>
            <div class="wcd-custom-time-info mt-2">
              <v-icon x-small color="rgba(255,255,255,0.3)" class="mr-1">mdi-pencil-outline</v-icon>
              <span>Oder eigene Startzeit direkt im Formular eintragen.</span>
            </div>
          </div>

          <!-- Column 3: Summary Preview Card -->
          <div class="flex-grow-1" style="min-width: 220px; max-width: 320px">
            <div
              class="wcd-summary-card pa-3 rounded-lg"
              :style="{
                background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                border: isDarkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.08)',
              }">
              <div class="text-caption font-weight-bold mb-1" :class="isDarkMode ? 'grey--text text--lighten-2' : 'grey--text text--darken-2'">
                Buchungsvorschau
              </div>
              <div class="d-flex align-center justify-space-between mt-2">
                <span class="text-caption font-weight-bold" :class="isDarkMode ? 'white--text' : 'black--text'">Zeit:</span>
                <span class="text-caption font-weight-black text-shadow" :style="{ color: start ? 'var(--v-primary-base)' : 'inherit' }">
                  {{ start ? start + " - " + calculatedEnd : "Keine Startzeit gewählt" }}
                </span>
              </div>
              <div class="d-flex align-center justify-space-between mt-1">
                <span class="text-caption" :class="isDarkMode ? 'grey--text' : 'grey--text text--darken-1'">Dauer:</span>
                <span class="text-caption font-weight-bold" :class="isDarkMode ? 'white--text' : 'black--text'">{{ duration }} Min</span>
              </div>
              <div v-if="start" class="mt-2">
                <div v-if="isSelectedSlotOptimal" class="d-flex align-center text-caption success--text font-weight-bold" style="gap: 4px">
                  <v-icon x-small color="success">mdi-check-decagram</v-icon>
                  <span>Optimaler Anschluss (Lückenlos)</span>
                </div>
                <div v-else class="d-flex align-center text-caption info--text font-weight-medium" style="gap: 4px; color: #81c784 !important">
                  <v-icon x-small color="#81c784">mdi-clock</v-icon>
                  <span>Freier Termin</span>
                </div>
              </div>
            </div>
            <v-btn v-if="start" block small depressed color="success" class="mt-3 text-none font-weight-bold" @click="$emit('select-and-close')">
              <v-icon left x-small>mdi-check</v-icon>
              Termin übernehmen
            </v-btn>
          </div>
        </div>

        <!-- Mobile Layout (Stacked columns, optimized for Touch/Tablet) -->
        <div v-else>
          <div class="d-flex align-center mb-2" style="gap: 8px">
            <v-avatar size="24" style="border: 1px solid var(--v-primary-base)">
              <img :src="getEmployeePictureUrl(activeSegment.colPicture)" alt="avatar" />
            </v-avatar>
            <span class="font-weight-bold" :class="isDarkMode ? 'white--text' : 'grey--text text--darken-3'" style="font-size: 0.8rem">
              {{ activeSegment.colName }} • {{ formatDateFriendly(activeSegment.date) }}
            </span>
          </div>

          <div class="d-flex align-center mb-3" style="gap: 6px; font-size: 0.72rem; color: #81c784">
            <v-icon x-small color="#81c784">mdi-clock-outline</v-icon>
            <span>Frei: {{ minutesToTimeStr(activeSegment.startMin) }} - {{ minutesToTimeStr(activeSegment.endMin) }}</span>
          </div>

          <div class="wcd-shortcuts-title mb-2">Startzeit wählen:</div>
          <div class="wcd-shortcuts-grid mb-2">
            <div
              v-for="slot in activeSegmentSlots"
              :key="slot.time"
              class="wcd-shortcut-btn d-flex align-center"
              :class="{ active: start === slot.time, 'optimal-connect': slot.isOptimal }"
              @click="selectShortcutTime(slot.time)">
              <span>{{ slot.time }}</span>
              <v-icon v-if="slot.isOptimal" x-small color="success" class="ml-1" style="font-size: 10px">mdi-check-decagram</v-icon>
            </div>
            <div v-if="activeSegmentSlots.length === 0" class="grey--text text-caption py-1">Keine passende Startzeit vorhanden.</div>
          </div>

          <div v-if="start" class="mt-2 text-caption">
            <span class="font-weight-medium" :class="isDarkMode ? 'white--text' : 'black--text'">Vorschau: </span>
            <span class="font-weight-bold primary--text">{{ start }} - {{ calculatedEnd }}</span>
            <span v-if="isSelectedSlotOptimal" class="success--text font-weight-bold ml-1">(Optimaler Anschluss)</span>

            <v-btn block small depressed color="success" class="mt-3 text-none font-weight-bold" @click="$emit('select-and-close')">
              <v-icon left x-small>mdi-check</v-icon>
              Termin übernehmen
            </v-btn>
          </div>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
import moment from "moment"
import api from "@/api"

export default {
  name: "WorkCalendarAppointmentFinder",
  props: {
    employees: { type: Array, required: true },
    date: { type: String, default: "" },
    start: { type: String, default: "" },
    colId: { type: [String, Number], default: "" },
    duration: { type: Number, default: 60 },
    isDarkMode: { type: Boolean, default: true },
    excludeAppointmentId: { type: [String, Number], default: null },
    hideTitle: { type: Boolean, default: false },
  },
  data() {
    return {
      finderStartDate: "",
      finderDateMenu: false,
      finderIgnoreTasks: false,
      loadingSlots: false,
      suggestedSlots: [],
      finderColId: "all",
      activeSegment: null,
      isDragging: false,
      dragStartClientX: 0,
      dragStartMin: 0,
      dragBarRect: null,
      dragContext: null,

      filterTimeOfDay: "any",
      filterWeekdays: [1, 2, 3, 4, 5, 6, 0],
      timelineStartHour: 8,
      timelineEndHour: 20,
      dailyColumns: {},

      weekdayOptions: [
        { label: "Mo", value: 1 },
        { label: "Di", value: 2 },
        { label: "Mi", value: 3 },
        { label: "Do", value: 4 },
        { label: "Fr", value: 5 },
        { label: "Sa", value: 6 },
        { label: "So", value: 0 },
      ],
      timeOfDayOptions: [
        { label: "Beliebig", value: "any", icon: "mdi-calendar-range" },
        { label: "Vormittag", value: "morning", icon: "mdi-weather-sunset-up" },
        { label: "Nachmittag", value: "afternoon", icon: "mdi-weather-sunny" },
        { label: "Abend", value: "evening", icon: "mdi-weather-night" },
      ],
    }
  },
  watch: {
    date: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal !== this.finderStartDate) {
          this.finderStartDate = newVal
        }
      },
    },
    finderStartDate(newVal, oldVal) {
      if (this.isInternalDateChange) {
        return
      }
      this.activeSegment = null
      this.searchFreeSlots()
    },
    finderIgnoreTasks() {
      this.activeSegment = null
      this.searchFreeSlots()
    },
    duration() {
      this.activeSegment = null
      this.searchFreeSlots()
    },
    finderColId() {
      this.activeSegment = null
      this.searchFreeSlots()
    },
    filterTimeOfDay() {
      this.activeSegment = null
      this.searchFreeSlots()
    },
    filterWeekdays() {
      this.activeSegment = null
      this.searchFreeSlots()
    },
  },
  computed: {
    calculatedEnd() {
      if (!this.start) return "-"
      return moment(this.start, "HH:mm").add(this.duration, "minutes").format("HH:mm")
    },
    isTouchDevice() {
      if (typeof window === "undefined") return false
      return "ontouchstart" in window || navigator.maxTouchPoints > 0
    },
    slotsGroupedByDay() {
      const groups = {}
      this.suggestedSlots.forEach((slot) => {
        if (!groups[slot.date]) {
          groups[slot.date] = []
        }
        groups[slot.date].push(slot)
      })

      // Sort dates chronologically to ensure they display in correct order
      const allDates = Object.keys(this.dailyColumns).sort()

      return allDates.map((date) => {
        const dayCols = this.dailyColumns[date] || []

        let displayCols = dayCols
        if (this.finderColId && this.finderColId !== "all") {
          displayCols = dayCols.filter((col) => {
            const colId = col.info?._id || col.id
            return String(colId) === String(this.finderColId)
          })
        }

        const timelines = displayCols.map((col) => {
          return {
            id: col.info?._id || col.id,
            name: col.firstName || (col.info ? `${col.info.firstName || ""} ${col.info.lastName || ""}`.trim() : ""),
            picture: col.picture || (col.info ? col.info.picture : null),
            segments: this.getEmployeeDaySegments(col, date),
          }
        })

        return {
          date,
          dayName: moment(date).format("dddd"),
          slots: (groups[date] || []).slice(0, 8),
          timelines,
        }
      })
    },
    activeSegmentSlots() {
      if (!this.activeSegment) return []
      const durationMin = this.duration || 30
      const startMin = this.activeSegment.startMin
      const endMin = this.activeSegment.endMin

      const emp = this.employees.find((e) => e.id === this.activeSegment.colId)
      const timeToMinutes = (tStr) => {
        if (!tStr) return 0
        const [h, m] = tStr.split(":").map(Number)
        return h * 60 + m
      }

      const blockStartAndEnds = new Set()
      if (emp) {
        if (emp.appointments) {
          emp.appointments.forEach((a) => {
            if (this.excludeAppointmentId && (a._id === this.excludeAppointmentId || a.id === this.excludeAppointmentId)) {
              return
            }
            if (a.status !== "cancelled" && a.status !== "noshow") {
              blockStartAndEnds.add(timeToMinutes(a.start))
              blockStartAndEnds.add(timeToMinutes(a.end))
            }
          })
        }
        if (emp.tasks && !this.finderIgnoreTasks) {
          emp.tasks.forEach((t) => {
            const dur = t.duration || 30
            const startM = timeToMinutes(t.time)
            blockStartAndEnds.add(startM)
            blockStartAndEnds.add(startM + dur)
          })
        }
        if (emp.shifts) {
          emp.shifts.forEach((s) => {
            if (s.isSick) return
            const times = s.times || []
            times.forEach((t) => {
              if (t.type !== "Pause") {
                blockStartAndEnds.add(timeToMinutes(t.start))
                blockStartAndEnds.add(timeToMinutes(t.end))
              }
            })
          })
        }
      }

      const slots = []
      for (let sM = startMin; sM <= endMin - durationMin; sM += 30) {
        const h = Math.floor(sM / 60)
        const m = sM % 60
        const time = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`

        const eh = Math.floor((sM + durationMin) / 60)
        const em = (sM + durationMin) % 60
        const endTime = `${String(eh).padStart(2, "0")}:${String(em).padStart(2, "0")}`

        const isOptimal = blockStartAndEnds.has(sM) || blockStartAndEnds.has(sM + durationMin)

        slots.push({
          time,
          endTime,
          startMin: sM,
          isOptimal,
        })
      }
      return slots
    },
    isSelectedSlotOptimal() {
      if (!this.start || !this.activeSegmentSlots.length) return false
      const slot = this.activeSegmentSlots.find((s) => s.time === this.start)
      return slot ? slot.isOptimal : false
    },
    activeSelectionStyle() {
      if (!this.start || !this.activeSegment) return { display: "none" }
      const [h, m] = this.start.split(":").map(Number)
      if (isNaN(h) || isNaN(m)) return { display: "none" }
      const startMin = h * 60 + m
      const timelineStartMin = this.timelineStartHour * 60
      const timelineEndMin = this.timelineEndHour * 60
      const totalMin = timelineEndMin - timelineStartMin

      const leftPct = ((startMin - timelineStartMin) / totalMin) * 100
      const widthPct = (this.duration / totalMin) * 100

      return {
        left: `${leftPct}%`,
        width: `${widthPct}%`,
      }
    },
  },
  created() {
    this.dailyViewCache = {}
    this.isInternalDateChange = false
  },
  mounted() {
    if (!this.suggestedSlots.length && this.finderStartDate) {
      this.searchFreeSlots()
    }
  },
  methods: {
    toggleWeekday(val) {
      if (this.filterWeekdays.includes(val)) {
        if (this.filterWeekdays.length > 1) {
          this.filterWeekdays = this.filterWeekdays.filter((w) => w !== val)
        }
      } else {
        this.filterWeekdays.push(val)
      }
    },
    selectFreeSegment(date, timeline, segment) {
      const minutesToTime = (mins) => {
        const h = Math.floor(mins / 60)
        const m = mins % 60
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
      }
      const timeStr = minutesToTime(segment.start)

      this.isInternalDateChange = true
      this.$emit("update:date", date)
      this.$emit("update:start", timeStr)
      this.$emit("update:col-id", timeline.id)

      this.activeSegment = {
        date,
        colId: timeline.id,
        colName: timeline.name,
        colPicture: timeline.picture,
        startMin: segment.start,
        endMin: segment.end,
        label: segment.label,
      }

      this.$nextTick(() => {
        this.isInternalDateChange = false
      })
    },
    selectShortcutTime(time) {
      this.$emit("update:start", time)
    },
    minutesToTimeStr(mins) {
      const h = Math.floor(mins / 60)
      const m = mins % 60
      return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
    },
    getEmployeeDaySegments(col, date) {
      const startMin = this.timelineStartHour * 60
      const endMin = this.timelineEndHour * 60
      const totalMin = endMin - startMin

      const timeToMinutes = (tStr) => {
        const [h, m] = tStr.split(":").map(Number)
        return h * 60 + m
      }

      const shifts = col.shifts || []
      const isSick = col.isSick || false
      const absenceReason = col.absenceReason || null

      if (isSick || absenceReason) {
        return [{ type: "blocked", start: startMin, end: endMin, label: absenceReason || "Krank" }]
      }

      if (!shifts.length) {
        return [{ type: "out_of_service", start: startMin, end: endMin, label: "Kein Dienst" }]
      }

      let workIntervals = []
      shifts.forEach((s) => {
        if (s.isSick) return
        const times = s.times || []
        times.forEach((t) => {
          if (t.type !== "Pause") {
            const sM = Math.max(startMin, timeToMinutes(t.start))
            const eM = Math.min(endMin, timeToMinutes(t.end))
            if (eM > sM) {
              workIntervals.push({ start: sM, end: eM })
            }
          }
        })
      })

      if (!workIntervals.length) {
        return [{ type: "out_of_service", start: startMin, end: endMin, label: "Kein Dienst" }]
      }

      let currentSegments = []
      let lastEnd = startMin

      workIntervals.sort((a, b) => a.start - b.start)

      workIntervals.forEach((wi) => {
        if (wi.start > lastEnd) {
          currentSegments.push({ type: "out_of_service", start: lastEnd, end: wi.start, label: "Kein Dienst" })
        }
        currentSegments.push({ type: "free", start: wi.start, end: wi.end, label: "Frei" })
        lastEnd = wi.end
      })

      if (lastEnd < endMin) {
        currentSegments.push({ type: "out_of_service", start: lastEnd, end: endMin, label: "Kein Dienst" })
      }

      const hardBlockages = []
      const softBlockages = []

      if (col.appointments) {
        col.appointments.forEach((a) => {
          if (this.excludeAppointmentId && (a._id === this.excludeAppointmentId || a.id === this.excludeAppointmentId)) {
            return
          }
          if (a.status !== "cancelled" && a.status !== "noshow") {
            hardBlockages.push({
              start: timeToMinutes(a.start),
              end: timeToMinutes(a.end),
              title: a.title || "Termin",
            })
          }
        })
      }

      if (col.tasks) {
        col.tasks.forEach((t) => {
          const dur = t.duration || 30
          const startM = timeToMinutes(t.time)
          softBlockages.push({
            start: startM,
            end: startM + dur,
            title: t.title || "Aufgabe",
          })
        })
      }

      shifts.forEach((s) => {
        if (s.isSick) return
        const times = s.times || []
        times.forEach((t) => {
          if (t.type === "Pause") {
            hardBlockages.push({
              start: timeToMinutes(t.start),
              end: timeToMinutes(t.end),
              title: "Pause",
            })
          }
        })

        const isOpening =
          s.isOpeningShift !== undefined && s.isOpeningShift !== null ? s.isOpeningShift : s.shiftTemplate ? s.shiftTemplate.isOpeningShift : false
        const isClosing =
          s.isClosingShift !== undefined && s.isClosingShift !== null ? s.isClosingShift : s.shiftTemplate ? s.shiftTemplate.isClosingShift : false
        if (isOpening || isClosing) {
          const workTimes = times.filter((t) => t.type !== "Pause")
          const sorted = [...workTimes].sort((a, b) => a.start.localeCompare(b.start))
          if (sorted.length > 0) {
            if (isOpening) {
              const startStr = sorted[0].start
              hardBlockages.push({
                start: timeToMinutes(startStr),
                end: timeToMinutes(startStr) + 30,
                title: "Centeröffnung",
              })
            }
            if (isClosing) {
              const endStr = sorted[sorted.length - 1].end
              hardBlockages.push({
                start: timeToMinutes(endStr) - 30,
                end: timeToMinutes(endStr),
                title: "Centerschließung",
              })
            }
          }
        }
      })

      const subtractBlockage = (segs, bStart, bEnd, bType, bLabel) => {
        const result = []
        segs.forEach((seg) => {
          if (seg.type !== "free" || bEnd <= seg.start || bStart >= seg.end) {
            result.push(seg)
            return
          }
          if (seg.start < bStart) {
            result.push({ type: "free", start: seg.start, end: bStart, label: "Frei" })
          }
          const overlapStart = Math.max(seg.start, bStart)
          const overlapEnd = Math.min(seg.end, bEnd)
          result.push({ type: bType, start: overlapStart, end: overlapEnd, label: bLabel })
          if (seg.end > bEnd) {
            result.push({ type: "free", start: bEnd, end: seg.end, label: "Frei" })
          }
        })
        return result
      }

      hardBlockages.forEach((hb) => {
        currentSegments = subtractBlockage(currentSegments, hb.start, hb.end, "blocked", hb.title)
      })

      const taskSegmentType = this.finderIgnoreTasks ? "task" : "blocked"
      softBlockages.forEach((sb) => {
        currentSegments = subtractBlockage(currentSegments, sb.start, sb.end, taskSegmentType, sb.title)
      })

      // Determine active filter range
      let limitStart = startMin
      let limitEnd = endMin
      if (this.filterTimeOfDay === "morning") {
        limitStart = 480 // 8:00
        limitEnd = 720 // 12:00
      } else if (this.filterTimeOfDay === "afternoon") {
        limitStart = 720 // 12:00
        limitEnd = 1020 // 17:00
      } else if (this.filterTimeOfDay === "evening") {
        limitStart = 1020 // 17:00
        limitEnd = 1200 // 20:00
      }

      // Split segments based on filter range so they dim correctly
      let splitSegments = []
      currentSegments.forEach((s) => {
        if (s.type !== "free") {
          splitSegments.push(s)
          return
        }

        const sStart = s.start
        const sEnd = s.end

        if (sStart < limitStart) {
          const endPart = Math.min(sEnd, limitStart)
          splitSegments.push({ type: "out_of_filter", start: sStart, end: endPart, label: "Frei (Außerhalb Filter)" })
        }

        const overlapStart = Math.max(sStart, limitStart)
        const MathMinEndLimit = Math.min(sEnd, limitEnd)
        if (MathMinEndLimit > overlapStart) {
          splitSegments.push({ type: "free", start: overlapStart, end: MathMinEndLimit, label: "Frei" })
        }

        if (sEnd > limitEnd) {
          const startPart = Math.max(sStart, limitEnd)
          splitSegments.push({ type: "out_of_filter", start: startPart, end: sEnd, label: "Frei (Außerhalb Filter)" })
        }
      })
      currentSegments = splitSegments

      return currentSegments
        .filter((s) => s.end > s.start)
        .map((s) => {
          const sStart = Math.max(startMin, s.start)
          const sEnd = Math.min(endMin, s.end)
          const pct = ((sEnd - sStart) / totalMin) * 100

          const minutesToTime = (mins) => {
            const h = Math.floor(mins / 60)
            const m = mins % 60
            return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
          }

          let type = s.type
          let label = s.label
          const segmentDuration = sEnd - sStart
          const requiredDuration = this.duration || 30

          if (type === "free" && segmentDuration < requiredDuration) {
            type = "insufficient_time"
            label = "Frei aber zu wenig Zeit"
          }

          return {
            ...s,
            type,
            label,
            start: sStart,
            end: sEnd,
            widthPct: pct,
            timeStr: `${minutesToTime(sStart)} - ${minutesToTime(sEnd)}`,
          }
        })
    },
    async searchFreeSlots() {
      if (!this.finderStartDate) return
      this.loadingSlots = true
      try {
        const durationMin = this.duration || 30
        const searchDate = this.finderStartDate

        const timeToMinutes = (tStr) => {
          if (!tStr) return 0
          const [h, m] = tStr.split(":").map(Number)
          return h * 60 + m
        }

        const minutesToTime = (mins) => {
          const h = Math.floor(mins / 60)
          const m = mins % 60
          return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
        }

        const subtractInterval = (intervals, bStart, bEnd) => {
          const result = []
          for (const [start, end] of intervals) {
            if (bEnd <= start || bStart >= end) {
              result.push([start, end])
            } else {
              if (start < bStart) {
                result.push([start, bStart])
              }
              if (end > bEnd) {
                result.push([bEnd, end])
              }
            }
          }
          return result
        }

        let allSlots = []
        const dailyColsMap = {}

        const processDayView = (view, date) => {
          const dayOfWeek = moment(date).day()

          if (!this.filterWeekdays.includes(dayOfWeek)) {
            return
          }

          const columns = view.data?.columns || []

          const actualCols = columns.filter((col) => {
            const colId = col.info?._id || col.id
            return colId !== "ma_info" && colId !== "no_show" && !col.isHelper
          })

          dailyColsMap[date] = actualCols

          let checkCols = actualCols
          if (this.finderColId && this.finderColId !== "all") {
            checkCols = actualCols.filter((col) => {
              const colId = col.info?._id || col.id
              return String(colId) === String(this.finderColId)
            })
          }

          checkCols.forEach((col) => {
            const shifts = col.shifts || []
            const hardBlockages = []
            const softBlockages = []

            if (col.appointments) {
              col.appointments.forEach((a) => {
                if (this.excludeAppointmentId && (a._id === this.excludeAppointmentId || a.id === this.excludeAppointmentId)) {
                  return
                }
                if (a.status !== "cancelled" && a.status !== "noshow") {
                  hardBlockages.push({
                    start: timeToMinutes(a.start),
                    end: timeToMinutes(a.end),
                    title: a.title || "Termin",
                  })
                }
              })
            }

            if (col.tasks) {
              col.tasks.forEach((t) => {
                const dur = t.duration || 30
                const startM = timeToMinutes(t.time)
                softBlockages.push({
                  start: startM,
                  end: startM + dur,
                  title: t.title || "Aufgabe",
                })
              })
            }

            shifts.forEach((s) => {
              if (s.isSick || col.absenceReason) return
              const times = s.times || []
              if (!times.length) return

              times.forEach((t) => {
                if (t.type === "Pause") {
                  hardBlockages.push({
                    start: timeToMinutes(t.start),
                    end: timeToMinutes(t.end),
                    title: "Pause",
                  })
                }
              })

              const isOpening =
                s.isOpeningShift !== undefined && s.isOpeningShift !== null
                  ? s.isOpeningShift
                  : s.shiftTemplate
                  ? s.shiftTemplate.isOpeningShift
                  : false
              const isClosing =
                s.isClosingShift !== undefined && s.isClosingShift !== null
                  ? s.isClosingShift
                  : s.shiftTemplate
                  ? s.shiftTemplate.isClosingShift
                  : false
              if (isOpening || isClosing) {
                const workTimes = times.filter((t) => t.type !== "Pause")
                const sorted = [...workTimes].sort((a, b) => a.start.localeCompare(b.start))
                if (sorted.length > 0) {
                  if (isOpening) {
                    const startStr = sorted[0].start
                    hardBlockages.push({
                      start: timeToMinutes(startStr),
                      end: timeToMinutes(startStr) + 30,
                      title: "Centeröffnung",
                    })
                  }
                  if (isClosing) {
                    const endStr = sorted[sorted.length - 1].end
                    hardBlockages.push({
                      start: timeToMinutes(endStr) - 30,
                      end: timeToMinutes(endStr),
                      title: "Centerschließung",
                    })
                  }
                }
              }
            })

            shifts.forEach((s) => {
              if (s.isSick || col.absenceReason) return
              const times = s.times || []
              if (!times.length) return

              let workIntervals = []
              times.forEach((t) => {
                if (t.type !== "Pause") {
                  workIntervals.push([timeToMinutes(t.start), timeToMinutes(t.end)])
                }
              })

              hardBlockages.forEach((b) => {
                workIntervals = subtractInterval(workIntervals, b.start, b.end)
              })

              if (!this.finderIgnoreTasks) {
                softBlockages.forEach((b) => {
                  workIntervals = subtractInterval(workIntervals, b.start, b.end)
                })
              }

              const validIntervals = workIntervals.filter(([start, end]) => end - start >= durationMin)

              validIntervals.forEach(([start, end]) => {
                for (let sM = start; sM <= end - durationMin; sM += 30) {
                  let matchesTime = true
                  if (this.filterTimeOfDay === "morning") {
                    matchesTime = sM >= 480 && sM < 720
                  } else if (this.filterTimeOfDay === "afternoon") {
                    matchesTime = sM >= 720 && sM < 1020
                  } else if (this.filterTimeOfDay === "evening") {
                    matchesTime = sM >= 1020 && sM < 1200
                  }

                  if (!matchesTime) continue

                  let isTaskConflict = false
                  let conflictTaskTitle = ""

                  if (this.finderIgnoreTasks) {
                    const matchingTask = softBlockages.find((tb) => tb.start < sM + durationMin && tb.end > sM)
                    if (matchingTask) {
                      isTaskConflict = true
                      conflictTaskTitle = matchingTask.title
                    }
                  }

                  allSlots.push({
                    date,
                    time: minutesToTime(sM),
                    endTime: minutesToTime(sM + durationMin),
                    colId: col.info?._id || col.id,
                    colName: col.firstName || (col.info ? `${col.info.firstName || ""} ${col.info.lastName || ""}`.trim() : ""),
                    colPicture: col.picture || (col.info ? col.info.picture : null),
                    isTaskConflict,
                    conflictTaskTitle,
                    startMin: sM,
                  })
                }
              })
            })
          })
        }

        const fetchCachedDailyView = (date) => {
          if (!this.dailyViewCache[date]) {
            this.dailyViewCache[date] = api.shiftManagement
              .fetchDailyView(date)
              .then((res) => res)
              .catch(() => {
                delete this.dailyViewCache[date]
                return { data: { columns: [] } }
              })
          }
          return this.dailyViewCache[date]
        }

        // Phase 1: Search the next 7 days
        const phase1Dates = []
        for (let i = 0; i < 7; i++) {
          phase1Dates.push(moment(searchDate).clone().add(i, "days").format("YYYY-MM-DD"))
        }

        const phase1Views = await Promise.all(phase1Dates.map((date) => fetchCachedDailyView(date)))

        phase1Views.forEach((view, idx) => {
          processDayView(view, phase1Dates[idx])
        })

        // Phase 2: If we found less than 5 free slots, search another 7 days (days 8 to 14)
        if (allSlots.length < 5) {
          const phase2Dates = []
          for (let i = 7; i < 14; i++) {
            phase2Dates.push(moment(searchDate).clone().add(i, "days").format("YYYY-MM-DD"))
          }

          const phase2Views = await Promise.all(phase2Dates.map((date) => fetchCachedDailyView(date)))

          phase2Views.forEach((view, idx) => {
            processDayView(view, phase2Dates[idx])
          })
        }

        allSlots.sort((a, b) => {
          if (a.isTaskConflict !== b.isTaskConflict) {
            return a.isTaskConflict ? 1 : -1
          }
          if (a.date !== b.date) {
            return a.date.localeCompare(b.date)
          }
          return a.startMin - b.startMin
        })

        this.dailyColumns = dailyColsMap
        this.suggestedSlots = allSlots
      } catch (err) {
        console.error("Fehler beim Suchen freier Termine:", err)
        this.suggestedSlots = []
      } finally {
        this.loadingSlots = false
      }
    },
    getEmployeePictureUrl(pic) {
      return pic ? "/img/profilbilder/" + pic : "/img/icons/user_placeholder.png"
    },
    formatDateFriendly(dateStr) {
      if (!dateStr) return ""
      return moment(dateStr).format("DD.MM.YYYY")
    },
    initiateDrag(event, date, timeline) {
      event.preventDefault()

      this.dragContext = {
        date,
        timeline,
        activeSeg: { ...this.activeSegment },
      }

      const clientX = event.type.startsWith("touch") ? event.touches[0].clientX : event.clientX

      const barEl = event.currentTarget.closest(".wcd-timeline-bar")
      if (!barEl) return

      const rect = barEl.getBoundingClientRect()

      this.dragStartClientX = clientX
      this.dragBarRect = rect

      const [h, m] = this.start.split(":").map(Number)
      this.dragStartMin = h * 60 + m

      this.isDragging = true

      if (event.type.startsWith("touch")) {
        document.addEventListener("touchmove", this.handleDrag, { passive: false })
        document.addEventListener("touchend", this.endDrag)
      } else {
        document.addEventListener("mousemove", this.handleDrag)
        document.addEventListener("mouseup", this.endDrag)
      }

      document.body.style.cursor = "grabbing"
      document.body.style.userSelect = "none"
    },
    handleDrag(event) {
      if (!this.isDragging || !this.dragBarRect || !this.dragContext) return

      if (event.cancelable) {
        event.preventDefault()
      }

      const clientX = event.type.startsWith("touch") ? event.touches[0].clientX : event.clientX
      const deltaX = clientX - this.dragStartClientX

      const timelineStartMin = this.timelineStartHour * 60
      const timelineEndMin = this.timelineEndHour * 60
      const totalMin = timelineEndMin - timelineStartMin

      const deltaPct = deltaX / this.dragBarRect.width
      const deltaMin = deltaPct * totalMin

      let newStartMin = this.dragStartMin + deltaMin

      // Snap to nearest 15 minutes
      newStartMin = Math.round(newStartMin / 15) * 15

      const limitStart = this.dragContext.activeSeg.startMin
      const limitEnd = this.dragContext.activeSeg.endMin
      const currentDuration = this.duration || 30

      const maxStartMin = limitEnd - currentDuration
      newStartMin = Math.max(limitStart, Math.min(maxStartMin, newStartMin))

      const h = Math.floor(newStartMin / 60)
      const m = newStartMin % 60
      const newTime = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
      this.$emit("update:start", newTime)
    },
    endDrag() {
      this.isDragging = false
      this.dragBarRect = null

      document.removeEventListener("mousemove", this.handleDrag)
      document.removeEventListener("mouseup", this.endDrag)
      document.removeEventListener("touchmove", this.handleDrag)
      document.removeEventListener("touchend", this.endDrag)

      document.body.style.cursor = ""
      document.body.style.userSelect = ""
    },
  },
}
</script>

<style scoped>
.wcd-appointment-finder {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
/* Scroll Area inside finder */
.wcd-finder-scroll-area {
  flex-grow: 1;
  overflow-y: auto;
  scrollbar-width: thin;
}

/* Date Display Menu button */
.wcd-date-display {
  display: flex;
  align-items: center;
  height: 36px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 0 10px;
  cursor: pointer;
  font-size: 0.8rem;
  color: white;
  transition: all 0.2s;
}
.wcd-date-display:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.12);
}

/* Custom Checkbox (capsule) */
.wcd-pill-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 0 10px;
  cursor: pointer;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
  transition: all 0.2s;
  user-select: none;
}
.wcd-pill-checkbox.active {
  background: color-mix(in srgb, var(--v-primary-base) 15%, transparent) !important;
  border-color: color-mix(in srgb, var(--v-primary-base) 40%, transparent) !important;
  color: var(--v-primary-base) !important;
}
.wcd-pill-checkbox:hover {
  border-color: rgba(255, 255, 255, 0.15);
}

/* Date warning notice box */
.wcd-date-notice {
  font-size: 0.75rem;
  background: color-mix(in srgb, var(--v-primary-base) 10%, transparent) !important;
  border: 1px solid color-mix(in srgb, var(--v-primary-base) 30%, transparent) !important;
  border-radius: 6px;
  padding: 6px 10px;
  color: var(--v-primary-base) !important;
  display: flex;
  align-items: center;
}

/* Time labels */
.wcd-time-label {
  font-size: 0.65rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.35);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

/* Time of day filter chips */
.wcd-time-chips {
  display: flex;
  gap: 6px;
  width: 100%;
}
.wcd-time-chip {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 4px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
  user-select: none;
}
.light-theme .wcd-time-chip:hover,
.wcd-time-chip:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.8);
}
.wcd-time-chip.active {
  background: color-mix(in srgb, var(--v-primary-base) 15%, transparent) !important;
  border-color: color-mix(in srgb, var(--v-primary-base) 40%, transparent) !important;
  color: var(--v-primary-base) !important;
}

/* Weekday selector chips */
.wcd-weekday-selector {
  display: flex;
  gap: 4px;
  width: 100%;
}
.wcd-weekday-chip {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 34px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 700;
  user-select: none;
}
.light-theme .wcd-weekday-chip:hover,
.wcd-weekday-chip:hover {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.8);
}
.wcd-weekday-chip.active {
  background: rgba(76, 175, 80, 0.22) !important;
  border-color: rgba(76, 175, 80, 0.55) !important;
  color: #81c784 !important;
}

/* Visual timeline styles */
.wcd-timelines-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  padding: 8px;
  border: 1px solid rgba(255, 255, 255, 0.03);
}
.wcd-timeline-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 4px 0;
}
.wcd-timeline-emp-info {
  display: flex;
  align-items: center;
  width: 150px;
  flex-shrink: 0;
}
.wcd-timeline-emp-name {
  font-size: 0.8rem;
  font-weight: 700;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 115px;
}
.wcd-timeline-bar-wrapper {
  flex-grow: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Ruler numbers */
.wcd-timeline-ruler {
  position: relative;
  height: 12px;
  width: 100%;
}
.wcd-ruler-tick {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  font-size: 0.65rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.35);
  line-height: 1;
}

/* The actual bar representing hours */
.wcd-timeline-bar {
  display: flex;
  height: 30px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.wcd-segment {
  height: 100%;
  position: relative;
  cursor: default;
  transition: all 0.2s ease;
}
.wcd-segment.segment-free {
  background: linear-gradient(135deg, #388e3c, #2e7d32) !important;
  cursor: pointer;
}
.wcd-segment.segment-blocked {
  background: rgba(255, 255, 255, 0.04);
}
.wcd-segment.segment-out_of_service {
  background: rgba(0, 0, 0, 0.3);
}
.wcd-segment.segment-task {
  background: linear-gradient(135deg, #ffa726, #f57c00) !important;
}
.wcd-segment.segment-insufficient_time {
  background: linear-gradient(135deg, #ff7043, #f4511e) !important;
}
.wcd-segment.segment-out_of_filter {
  background: rgba(255, 255, 255, 0.02) !important;
  opacity: 0.25;
}

/* Timeline grid lines overlay */
.wcd-timeline-grid-lines {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 3;
}
.wcd-grid-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(255, 255, 255, 0.12);
}

/* Hover highlight on free slots */
.wcd-segment-highlight {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition: background 0.15s ease;
}
.wcd-segment.segment-free:hover .wcd-segment-highlight {
  background: rgba(255, 255, 255, 0.15) !important;
}

/* Loader and Empty states */
.wcd-loader-container,
.wcd-empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 10px;
}

/* Groups and Headers */
.wcd-day-group {
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  padding: 10px;
}
.wcd-day-group:last-child {
  border-bottom: none;
}

/* Active Segment Box */
.wcd-active-segment-box {
  background: color-mix(in srgb, var(--v-primary-base) 8%, #1a1a1a) !important;
  border-top: 3px solid var(--v-primary-base) !important;
  box-shadow: 0 -6px 20px rgba(0, 0, 0, 0.35) !important;
  position: relative;
  z-index: 10;
}
.wcd-shortcuts-title {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--v-primary-base) !important;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}
.wcd-shortcuts-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.wcd-shortcut-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}
.light-theme .wcd-shortcut-btn:hover,
.wcd-shortcut-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
  color: white;
}
.wcd-shortcut-btn.active {
  background: rgba(76, 175, 80, 0.22) !important;
  border-color: rgba(76, 175, 80, 0.55) !important;
  color: #81c784 !important;
  box-shadow: 0 0 10px rgba(76, 175, 80, 0.25);
}
.wcd-custom-time-info {
  display: flex;
  align-items: center;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.3);
}

.wcd-pill-range {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: rgba(76, 175, 80, 0.08);
  border: 1px solid rgba(76, 175, 80, 0.2);
  border-radius: 20px;
  font-size: 0.7rem;
  font-weight: 700;
  color: #81c784;
}

.wcd-summary-card {
  transition: all 0.25s ease;
}
.wcd-shortcut-btn.optimal-connect {
  border-color: rgba(76, 175, 80, 0.3) !important;
}
.wcd-shortcut-btn.optimal-connect:hover {
  border-color: rgba(76, 175, 80, 0.5) !important;
}

/* Drag Selection Overlay Styles */
.wcd-active-selection-overlay {
  position: absolute;
  top: -1px;
  bottom: -1px;
  background: color-mix(in srgb, var(--v-primary-base) 85%, transparent) !important;
  border: 2px solid var(--v-primary-base) !important;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 8;
  cursor: grab;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.4);
  user-select: none;
  transition: box-shadow 0.2s;
}
.wcd-active-selection-overlay:active {
  cursor: grabbing;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.6);
}
.wcd-drag-handle-left,
.wcd-drag-handle-right {
  width: 4px;
  height: 14px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 2px;
  margin: 0 6px;
  flex-shrink: 0;
}

/* Light Theme Overrides */
.light-theme .wcd-date-display {
  background: rgba(0, 0, 0, 0.02) !important;
  border-color: rgba(0, 0, 0, 0.08) !important;
  color: #1e1e1e !important;
}
.light-theme .wcd-date-display:hover {
  background: rgba(0, 0, 0, 0.04) !important;
}
.light-theme .wcd-pill-checkbox {
  background: rgba(0, 0, 0, 0.02) !important;
  border-color: rgba(0, 0, 0, 0.08) !important;
  color: rgba(0, 0, 0, 0.5) !important;
}
.light-theme .wcd-pill-checkbox.active {
  background: color-mix(in srgb, var(--v-primary-base) 15%, transparent) !important;
  border-color: color-mix(in srgb, var(--v-primary-base) 45%, transparent) !important;
  color: var(--v-primary-base) !important;
}
.light-theme .wcd-date-notice {
  background: color-mix(in srgb, var(--v-primary-base) 8%, transparent) !important;
  border-color: color-mix(in srgb, var(--v-primary-base) 25%, transparent) !important;
  color: var(--v-primary-base) !important;
}
.light-theme .wcd-time-label {
  color: rgba(0, 0, 0, 0.5) !important;
}
.light-theme .wcd-time-chip {
  background: rgba(0, 0, 0, 0.02) !important;
  border-color: rgba(0, 0, 0, 0.08) !important;
  color: rgba(0, 0, 0, 0.5) !important;
}
.light-theme .wcd-time-chip.active {
  background: color-mix(in srgb, var(--v-primary-base) 15%, transparent) !important;
  border-color: color-mix(in srgb, var(--v-primary-base) 45%, transparent) !important;
  color: var(--v-primary-base) !important;
}
.light-theme .wcd-weekday-chip {
  background: rgba(0, 0, 0, 0.02) !important;
  border-color: rgba(0, 0, 0, 0.08) !important;
  color: rgba(0, 0, 0, 0.5) !important;
}
.light-theme .wcd-weekday-chip.active {
  background: rgba(46, 125, 50, 0.18) !important;
  border-color: rgba(46, 125, 50, 0.45) !important;
  color: #2e7d32 !important;
}
.light-theme .wcd-timeline-emp-name {
  color: #1e1e1e !important;
}
.light-theme .wcd-ruler-tick {
  color: rgba(0, 0, 0, 0.5) !important;
}
.light-theme .wcd-timeline-bar {
  background: rgba(0, 0, 0, 0.03) !important;
  border-color: rgba(0, 0, 0, 0.06) !important;
}
.light-theme .wcd-segment.segment-blocked {
  background: rgba(0, 0, 0, 0.06) !important;
}
.light-theme .wcd-segment.segment-out_of_service {
  background: rgba(0, 0, 0, 0.12) !important;
}
.light-theme .wcd-segment.segment-free {
  background: linear-gradient(135deg, #4caf50, #388e3c) !important;
}
.light-theme .wcd-segment.segment-free:hover .wcd-segment-highlight {
  background: rgba(255, 255, 255, 0.2) !important;
}
.light-theme .wcd-segment.segment-insufficient_time {
  background: linear-gradient(135deg, #ff8a65, #ff7043) !important;
}
.light-theme .wcd-segment.segment-task {
  background: linear-gradient(135deg, #ffb74d, #ffa726) !important;
}
.light-theme .wcd-segment.segment-out_of_filter {
  background: rgba(0, 0, 0, 0.02) !important;
  opacity: 0.2;
}
.light-theme .wcd-grid-line {
  background: rgba(0, 0, 0, 0.08) !important;
}
.light-theme .wcd-timelines-container {
  background: rgba(0, 0, 0, 0.02) !important;
  border-color: rgba(0, 0, 0, 0.05) !important;
}
.light-theme .wcd-active-segment-box {
  background: color-mix(in srgb, var(--v-primary-base) 5%, #ffffff) !important;
  border-top: 3px solid var(--v-primary-base) !important;
  box-shadow: 0 -6px 20px rgba(0, 0, 0, 0.08) !important;
}
.light-theme .wcd-shortcuts-title {
  color: var(--v-primary-base) !important;
}
.light-theme .wcd-shortcut-btn {
  background: rgba(0, 0, 0, 0.02) !important;
  border-color: rgba(0, 0, 0, 0.08) !important;
  color: rgba(0, 0, 0, 0.7) !important;
}
.light-theme .wcd-shortcut-btn:hover {
  background: rgba(0, 0, 0, 0.04) !important;
  color: #000000 !important;
}
.light-theme .wcd-shortcut-btn.active {
  background: rgba(46, 125, 50, 0.18) !important;
  border-color: rgba(46, 125, 50, 0.45) !important;
  color: #2e7d32 !important;
}
.light-theme .wcd-custom-time-info {
  color: rgba(0, 0, 0, 0.4) !important;
}
.light-theme .wcd-pill-range {
  background: rgba(46, 125, 50, 0.06) !important;
  border-color: rgba(46, 125, 50, 0.25) !important;
  color: #2e7d32 !important;
}
.light-theme .wcd-shortcut-btn.optimal-connect {
  border-color: rgba(46, 125, 50, 0.35) !important;
}
.light-theme .wcd-shortcut-btn.optimal-connect:hover {
  border-color: rgba(46, 125, 50, 0.55) !important;
}
.light-theme .wcd-active-selection-overlay {
  background: color-mix(in srgb, var(--v-primary-base) 90%, transparent) !important;
  border-color: var(--v-primary-base) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2) !important;
}
</style>
